<template>
  <v-container class="barriers" fluid>
    <div class="barriers-header">
      <h1 class="barriers-header__title">{{ $route.meta.title }}</h1>
      <div class="barriers-header__actions">
        <v-menu
          v-if="$checkPermission('store_barrier')"
          bottom
          left
          offset-y
          origin="bottom left"
          transition="scale-transition"
        >
          <template v-slot:activator="{ on, attrs }">
            <v-btn
              color="primary"
              outlined
              class="barriers-header__btn text-none"
              dark
              rounded
              v-bind="attrs"
              v-on="on"
            >
              <v-icon left>mdi-download</v-icon>
              Скачать отчёт
            </v-btn>
          </template>
          <v-list dense>
            <v-list-item @click="downloadReport()">
              <v-list-item-icon class="mr-2">
                <v-icon small>mdi-file-document-outline</v-icon>
              </v-list-item-icon>
              <v-list-item-content>
                <v-list-item-title>Полный отчёт</v-list-item-title>
              </v-list-item-content>
            </v-list-item>
            <v-list-item @click="downloadReportPage()">
              <v-list-item-icon class="mr-2">
                <v-icon small>mdi-file-table-outline</v-icon>
              </v-list-item-icon>
              <v-list-item-content>
                <v-list-item-title>Отчёт на странице</v-list-item-title>
              </v-list-item-content>
            </v-list-item>
          </v-list>
        </v-menu>
        <v-btn
          v-if="$checkPermission('store_barrier')"
          color="primary"
          class="barriers-header__btn text-none"
          dark
          rounded
          to="/barrier/new"
        >
          <v-icon left>mdi-plus</v-icon>
          Новый шлагбаум
        </v-btn>
      </div>
    </div>

    <v-card class="barriers-block mt-3" flat outlined>
      <div class="barriers-filters">
        <div class="barriers-filters__top-row">
          <div class="barriers-filters__title">
            <v-icon left small>mdi-filter-variant</v-icon>
            Фильтры
          </div>
          <v-btn
            color="primary"
            class="text-none barriers-filters__search-btn"
            dark
            rounded
            @click.prevent="filterBarriers"
          >
            <v-icon left>mdi-magnify</v-icon>
            Поиск
          </v-btn>
        </div>
        <div class="barriers-filters__row">
          <div class="barriers-filters__fields">
            <v-text-field
              v-model="filterAddress"
              @keyup.enter="filterBarriers"
              placeholder="Улица"
              outlined
              dense
              hide-details
              prepend-inner-icon="mdi-rename-box-outline"
              class="barriers-filters__field"
            />
            <v-autocomplete
              v-model="sipStatusSearched"
              placeholder="Статус"
              :items="sipSelectList"
              item-text="text"
              item-value="value"
              prepend-inner-icon="mdi-progress-clock"
              outlined
              dense
              hide-details
              clearable
              class="barriers-filters__field"
            />
            <v-autocomplete
              v-model="filterType"
              placeholder="Тип проезда"
              :items="typeBarriers"
              item-text="name"
              item-value="value"
              prepend-inner-icon="mdi-alert-circle-outline"
              outlined
              dense
              hide-details
              clearable
              class="barriers-filters__field"
            />
            <v-autocomplete
              v-if="disableFilterName"
              v-model="filterManagementCompany"
              placeholder="Управляющая компания"
              :items="managementCompanies"
              item-text="name"
              item-value="id"
              prepend-inner-icon="mdi-home-account"
              outlined
              dense
              hide-details
              clearable
              class="barriers-filters__field"
            />
          </div>
          <div class="barriers-filters__columns">
            <column-selector
              :visible-columns.sync="visibleColumns"
              :available-columns="barriersSettings.available_columns"
              :all-columns="allColumns"
              table-key="barriers"
            />
          </div>
        </div>
      </div>
      <v-divider class="my-0" />
      <v-progress-linear v-if="loadingSettings" indeterminate color="primary" />
      <v-data-table
        v-if="!loadingSettings"
        class="table row-click"
        :headers="headersBarriers"
        :items="barriers"
        :options.sync="options"
        :server-items-length="totalBarriers"
        :footer-props="{
          'items-per-page-options': [10, 20, 50, 100, 500],
          showFirstLastPage: true,
        }"
        @click:row="rowDetailBarrier"
      >
        <template v-slot:header.snapshotImage v-if="visibleColumns.includes('snapshotImage')">
          Изображение
          <v-icon @click="showSnapshot = !showSnapshot">mdi-image</v-icon>
        </template>
        <template v-slot:item.address="{ item }" v-if="!isDisplaySmall && visibleColumns.includes('address')">
          {{ item.address }}{{ getPassageType(item.passage_type) }}
          <span v-if="item.physical_status">
              <v-tooltip bottom>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    icon
                    v-bind="attrs"
                    v-on="on"
                    width="18"
                    height="18"
                  >
                    <v-icon x-small :color="physicalStatusColor(item.physical_status)">mdi-checkbox-blank-circle</v-icon>
                  </v-btn>
                </template>
                <span>{{ physicalStatus(item.physical_status, true) }}</span>
              </v-tooltip>
            </span>
          <div v-if="item.additional_addresses && item.additional_addresses.length">
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  icon
                  color="black"
                  v-bind="attrs"
                  v-on="on"
                  width="18"
                  height="18"
                  @click.stop="openAdditionalAddressesModal(item.additional_addresses)"
                >
                  <v-icon>mdi-information-slab-circle-outline</v-icon>
                </v-btn>
              </template>
              <span>Дополнительные адреса</span>
            </v-tooltip>
          </div>
        </template>
        <template v-slot:item.sip_status="{ item }" v-if="!isDisplaySmall && visibleColumns.includes('sip_status')">
          <div>
            <v-icon x-small :color="item.sip_status_color">
              mdi-checkbox-blank-circle
            </v-icon>
            {{ item.sip_status_text }}
          </div>
        </template>
        <template v-slot:item.passageType="{ item }" v-if="!isDisplaySmall && visibleColumns.includes('passage_type')">
          {{ getPassageType(item.passage_type) }}
        </template>
        <template v-slot:item.residential_complex_id="{ item }" v-if="!isDisplaySmall && visibleColumns.includes('residential_complex_id')">
          {{ getResidentialComplexName(item.residential_complex_id) }}
        </template>
        <template v-slot:item.opening_type="{ item }" v-if="!isDisplaySmall && visibleColumns.includes('opening_type')">
          <opening-type-text :opening-type="item.opening_type" />
        </template>
        <template v-slot:body v-if="isDisplaySmall">
          <tbody class="table" v-if="barriers[0]">
          <tr
            v-for="(item, index) in barriers" :key="index"
            @click.middle="clickBarrierWhell(item.id)"
            @click.prevent="clickBarrier($event, item.id)"
          >
            <td v-if="visibleColumns.includes('id')">{{ item.id }}</td>
            <td
              v-if="visibleColumns.includes('address')"
              :class="['pa-3', { 'd-flex justify-space-between': item.additional_addresses && item.additional_addresses.length }]"
            >
              <div>
                {{ item.address }}{{ getPassageType(item.passage_type) }}
                <span v-if="item.physical_status">
                  <v-tooltip bottom>
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn icon v-bind="attrs" v-on="on" width="18" height="18">
                        <v-icon x-small :color="physicalStatusColor(item.physical_status)">
                          mdi-checkbox-blank-circle
                        </v-icon>
                      </v-btn>
                    </template>
                    <span>{{ physicalStatus(item.physical_status, true) }}</span>
                  </v-tooltip>
                </span>
              </div>
              <div v-if="item.additional_addresses && item.additional_addresses.length">
                <v-tooltip bottom>
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn
                      icon
                      color="black"
                      v-bind="attrs"
                      v-on="on"
                      width="18"
                      height="18"
                      @click.stop="openAdditionalAddressesModal(item.additional_addresses)"
                    >
                      <v-icon>mdi-information-slab-circle-outline</v-icon>
                    </v-btn>
                  </template>
                  <span>Дополнительные адреса</span>
                </v-tooltip>
              </div>
            </td>
            <td v-if="visibleColumns.includes('name')">{{ item.name }}</td>
            <td v-if="visibleColumns.includes('opening_type')">
              <opening-type-text :opening-type="item.opening_type" />
            </td>
            <td v-if="visibleColumns.includes('management_company')">{{ item.management_company }}</td>
            <td v-if="visibleColumns.includes('management_group')">{{ item.management_group }}</td>
            <td v-if="visibleColumns.includes('sip_status')">
              <div>
                <v-icon x-small :color="item.sip_status_color">
                  mdi-checkbox-blank-circle
                </v-icon>
                {{ item.sip_status_text }}
              </div>
            </td>
            <td v-if="visibleColumns.includes('residential_complex_id')">
              {{ getResidentialComplexName(item.residential_complex_id) }}
            </td>
            <td v-if="visibleColumns.includes('snapshotImage')">
              <v-img
                v-if="showSnapshot"
                :src="`${item.snapshot}`"
                max-width="150"
                class="ml-auto mr-auto my-4"
                contain
              ></v-img>
            </td>
            <td v-if="visibleColumns.includes('model')">{{ item.model }}</td>
            <td v-if="visibleColumns.includes('barrier_open_phone')">{{ item.barrier_open_phone }}</td>
            <td v-if="visibleColumns.includes('comment')">{{ item.comment }}</td>
          </tr>
          </tbody>
          <tbody v-else>
          <tr>
            <td colspan="10" style="text-align: center">Отсутствуют данные</td>
          </tr>
          </tbody>
        </template>
      </v-data-table>
      <AdditionalAddressModal
        ref="additionalModal"
        :data-addresses="selectedAddresses"
        :is-check-size-small="isDisplaySmall"
        :is-adding-address="false"
      />
    </v-card>
  </v-container>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import ruDateFormatMixin from '@/mixins/ruDateFormat';
import StatusIntercomMixin from '@/mixins/StatusIntercomMixin';
import AdditionalAddressModal from '../components/barrier/DetailBarrier/components/GeneralInfo/components/AdditionalAddressModal.vue';
import ColumnSelector from '@/components/ColumnSelector.vue';
import OpeningTypeText from '@/components/barrier/OpeningTypeText';
import {
  formatOpeningType,
} from '@/components/barrier/consts';

export default {
  name: 'Barriers',
  components: { ColumnSelector, AdditionalAddressModal, OpeningTypeText },
  mixins: [ruDateFormatMixin, StatusIntercomMixin],
  data () {
    return {
      isCreated: false,
      getCallDoorphoneDialog: {},
      getCodeDoorDialog: {},
      filters: {},
      getResidentialComplexesIndividualDialog: {},
      resComplexsName: [],
      selectedAddresses: [],
      sipStatusSearched: '',
      disableRoleName: false,
      filterAddress: '',
      showSnapshot: false,
      filterManagementCompany: '',
      displayLoading: true,
      isDisplaySmall: true,
      disableFilterName: true,
      loadingSettings: false,
      totalBarriers: 0,
      filterType: '',
      visibleColumns: [],
      allColumns: [
        { text: 'id', value: 'id', width: '4%' },
        { text: 'Адрес', value: 'address', width: '28%' },
        { text: 'Название', value: 'name', width: '8%' },
        { text: 'Тип открытия', value: 'opening_type', width: '10%', sortable: false },
        { text: 'Управляющая компания', value: 'management_company', width: '14%' },
        { text: 'Компания-собственник', value: 'management_group', sortable: false },
        { text: 'Статус', value: 'sip_status', width: '13%' },
        { text: 'Жилой комплекс', value: 'residential_complex_id' },
        { text: 'Изображение', value: 'snapshotImage', sortable: false },
        { text: 'Модель шлагбаума', value: 'model', sortable: false },
        { text: 'Номер телефона для открытия', value: 'barrier_open_phone', sortable: false },
        { text: 'Комментарий', value: 'comment', sortable: false },
      ],
      sort: '-id',
      options: {
        itemsPerPage: 20,
        page: 1,
        sortBy: [],
        sortDesc: [],
      },
      typeBarriers: [
        {
          value: 'B',
          name: 'Шлагбаум',
        },
        {
          value: 'G',
          name: 'Ворота',
        },
      ],
      sipSelectList: [
        { text: 'В сети', value: '1' },
        { text: 'Не в сети', value: 'null' },
        { text: 'Без мониторинга', value: '5' },
      ],
    };
  },
  watch: {
    options: {
      handler () {
        if (!this.isCreated) {
          this.updateSort();
          this.loadBarriers();
        } else {
          this.isCreated = false;
        }
      },
      deep: true,
    },
    settingsTables: {
      immediate: true,
      async handler (val) {
        if (!val || !val.length || this.visibleColumns.length) return;
        const tableSettings = val.find(item => item.table_key === 'barriers');
        this.visibleColumns = Array.isArray(tableSettings?.visible_columns) && tableSettings.visible_columns.length
          ? [...tableSettings.visible_columns]
          : this.allColumns.map(col => col.value);

        this.loadingSettings = true;
        await new Promise(resolve => setTimeout(resolve, 300));
        this.loadingSettings = false;
      },
    },
  },
  created () {
    this.isCreated = true;
    if (Object.keys(this.$route.query).length) {
      const routeQuery = this.$route.query;

      if (routeQuery.address) {
        this.filterAddress = routeQuery.address;
        this.filters.address = routeQuery.address;
      }
      if (routeQuery.passage_type) {
        this.filterType = routeQuery.passage_type;
        this.filters.passage_type = routeQuery.passage_type;
      }
      if (routeQuery.management_company_id) {
        this.filterManagementCompany = routeQuery.management_company_id;
        this.filters.management_company_id = routeQuery.management_company_id;
      }
      if (routeQuery.status) {
        this.sipStatusSearched = routeQuery.status;
        this.filters.status = routeQuery.status;
      }
    }
    switch (this.roleName) {
      case 'call-center' : case 'manager' : case 'master' : this.disableFilterName = false;
        break;
    }
    this.getResidentialComplex()
      .then((data) => {
        this.resComplexsName = data.data;
      })
      .catch((error) => {
        console.error('Ошибка при получении данных:', error);
      });
    this.loadBarriers();
    if (this.disableFilterName) {
      this.loadManagementCompanies({
        filters: { with_doorphones: 1 },
      });
    }
    window.addEventListener('resize', this.checkSize);
    this.checkSize();
  },
  computed: {
    ...mapGetters({
      roleName: 'roleName',
      barriers: 'Barriers/barriers',
      settingsTables: 'User/settingsTables',
      managementCompanies: 'ManagementCompany/managementCompanies',
    }),
    barriersSettings () {
      return this.settingsTables.find(item => item.table_key === 'barriers') || {};
    },

    headersBarriers () {
      return this.allColumns
        .filter(col => this.visibleColumns.includes(col.value))
        .filter(col => col.value !== 'snapshotImage' || this.isDisplaySmall);
    },
  },
  methods: {
    ...mapActions({
      loadManagementCompanies: 'ManagementCompany/getManagementCompanies',
      loadAllBarriers: 'Barriers/loadAllBarriers',
      exportCsv: 'Barriers/exportCsv',
      getResidentialComplex: 'ResidentComplex/getComplex',
    }),
    openAdditionalAddressesModal (addresses) {
      this.selectedAddresses = addresses;
      this.$refs.additionalModal.open();
    },
    checkSize () {
      this.isDisplaySmall = window.innerWidth >= 600;
    },
    getResidentialComplexName (id) {
      const complex = this.resComplexsName.find(item => item.id === id);
      return complex ? complex.name : '';
    },
    getPassageType (item) {
      switch (item) {
        case 'B': return ', (Шлагбаум)';
        case 'G': return ', (Ворота)';
        default: return '';
      }
    },
    getOpeningType (item) {
      return formatOpeningType(item);
    },
    validateKeyUp (e) {
      if (e.keyCode === 13) {
        this.filterBarriers();
      }
    },
    clickBarrier (event, value) {
      if (this.$canRoute('DetailBarrier')) {
        if (event.ctrlKey) {
          this.clickBarrierWhell(value);
        } else {
          this.detailBarrier(value);
        }
      }
    },
    clickBarrierWhell (id) {
      const routeData = this.$router.resolve({ name: 'DetailBarrier', params: { id: id } });
      window.open(routeData.href, '_blank');
    },
    detailBarrier (value) {
      let { id } = value;
      if (!id) {
        id = value;
      }
      this.$router.push({ name: 'DetailBarrier', params: { id: id } });
    },
    rowDetailBarrier (value) {
      if (!this.isDisplaySmall) {
        this.detailBarrier(value);
      }
    },
    downloadReportPage () {
      const lines = [];
      this.barriers.forEach((item) => {
        const createdAt = this.ruDateFormat(item.installed_at, true);
        const passageType = this.getPassageType(item.passage_type);
        const openingType = this.getOpeningType(item.opening_type);
        const line = [
          item.id, item.address, createdAt, passageType, openingType,
          item.management_company, item.management_group, item.sip_status_text,
        ].join(';');
        lines.push(line);
      });
      const link = document.createElement('a');
      link.href = `data:text/csv;charset=CP1251,${this.encodeCP1251(lines.join('\n'))}`;
      link.download = 'Barriers.csv';

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    },
    downloadReport () {
      this.exportCsv()
        .then((response) => {
          const url = URL.createObjectURL(new Blob([response], {
            type: 'application/csv',
          }));
          const link = document.createElement('a');
          link.href = url;
          link.download = 'barriers.csv';
          document.body.appendChild(link);
          link.click();
        })
        .catch((e) => {
          this.$flash_warning('Ошибка при скачивании полного отчёта');
        });
    },
    updateSort () {
      if (this.options.sortBy.length > 0) {
        if ((this.options.sortDesc.length > 0) && (this.options.sortDesc[0])) {
          this.sort = '-' + this.options.sortBy[0];
        } else {
          this.sort = this.options.sortBy[0];
        }
      } else {
        this.sort = '';
      }
    },
    encodeCP1251 (string) {
      function encodeChar (c) {
        const isKyr = function (str) {
          return /[а-я]/i.test(str);
        };
        // eslint-disable-next-line no-multi-str
        const cp1251 = 'ЂЃ‚ѓ„…†‡€‰Љ‹ЊЌЋЏђ‘’“”•–—�™љ›њќћџ ЎўЈ¤Ґ¦§Ё©Є«¬*®Ї°±Ііґµ¶·\
ё№є»јЅѕїАБВГДЕЖЗИЙКЛМНОПРСТУФХЦЧШЩЪЫЬЭЮЯабвгдежзийклмнопрстуфхцчшщъыьэюя';
        const p = isKyr(c) ? (cp1251.indexOf(c) + 128) : c.charCodeAt(0);
        let h = p.toString(16);
        if (h === 'a') {
          h = '0A';
        }
        return '%' + h;
      }
      let res = '';
      for (let i = 0; i < string.length; i++) {
        res += encodeChar(string.charAt(i));
      }
      return res;
    },
    filterBarriers () {
      this.options.page = 1;
      this.filters = {};

      if (this.filterAddress) {
        this.filters.address = this.filterAddress;
      }
      if (this.sipStatusSearched) {
        this.filters.status = this.sipStatusSearched;
      }
      if (this.filterManagementCompany) {
        this.filters.management_company_id = this.filterManagementCompany;
      }
      if (this.filterType) {
        this.filters.passage_type = this.filterType;
      }
      this.$router.push({
        query: {
          address: this.filters.address,
          type: this.filters.passage_type,
          status: this.filters.status,
          managementCompany: this.filters.management_company_id,
        },
      }).catch(() => {});
      this.loadBarriers();
    },
    loadBarriers () {
      this.$router.push({
        query: {
          address: this.filters.address,
          type: this.filters.passage_type,
          status: this.filters.status,
          managementCompany: this.filters.management_company_id,
        },
      }).catch(() => {});
      this.loadAllBarriers({
        page: this.options.page,
        limit: this.options.itemsPerPage,
        sort: this.sort,
        filters: this.filters,
      })
        .then((response) => {
          this.totalBarriers = response.meta.total;
        })
        .catch((error) => {
          console.error(error);
          this.$sentry_exception(error);
          this.$flash_warning('Ошибка при загрузке');
        });
    },
  },
};
</script>

<style lang="scss" scoped>
.barriers-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px;
  padding: 8px 0;
}

.barriers-header__title {
  font-size: 1.5rem;
  font-weight: 400;
  color: #37474f;
  margin: 0;
  letter-spacing: -0.02em;
}

.barriers-header__actions {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
}

.barriers-header__btn {
  text-transform: none;
  font-weight: 500;
}

.barriers-block {
  border-radius: 12px;
  overflow: hidden;
}

.barriers-filters {
  padding: 16px 20px 12px;
}

.barriers-filters__top-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 12px;
}

.barriers-filters__title {
  font-size: 0.875rem;
  font-weight: 600;
  color: #546e7a;
  margin: 0;
  display: flex;
  align-items: center;
}

.barriers-filters__search-btn {
  flex-shrink: 0;
}

.barriers-filters__row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
}

.barriers-filters__fields {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
  flex: 1;
  min-width: 0;
}

.barriers-filters__field {
  max-width: 280px;
  width: 100%;
  min-width: 160px;
}

.barriers-filters__columns {
  flex-shrink: 0;
  align-self: center;
}

.barriers-filters__columns ::v-deep .v-btn {
  margin-right: 0;
}

.row-click {
  cursor: pointer;
}

.routerLink {
  text-decoration: none;
}

.v-application a {
  color: #434850;
}

a:hover.routerLink {
  text-decoration: underline;
}

@media screen and (max-width: 600px) {
  .barriers-header {
    flex-direction: column;
    align-items: flex-start;
  }

  .barriers-header__title {
    font-size: 1.4rem;
  }

  .barriers-filters__top-row {
    flex-direction: column;
    align-items: flex-start;
  }

  .barriers-filters__field {
    max-width: none;
  }

  .barriers-filters__row {
    flex-direction: column;
    align-items: stretch;
  }

  .barriers-filters__columns {
    align-self: flex-end;
  }
}
</style>
